import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  buildTripsCsv,
  DEFAULT_TRIP_PAGE_SIZE,
  isAmendedTrip,
  MAX_TRIP_PAGE_SIZE,
  TRIP_SORT_FIELDS,
  type SortDirection,
  type TripDto,
  type TripExportLabels,
  type TripExportRow,
  type TripListPage,
  type TripSortField,
  type TripStatus,
} from "@tc/shared";
import {
  Button,
  Icon,
  Pagination,
  Segmented,
  type SegmentedOption,
  SortHeader,
  StatusBadge,
  TRIP_STATUS_BADGE,
} from "@/components/ui";
import { tripsApi } from "@/features/trips/api";
import { STATUS_KEY } from "@/features/trips/status";
import { useClubFilter } from "@/lib/clubs/club-filter";
import { useClubLookup } from "@/lib/clubs/clubs";
import { useFormatters } from "@/lib/i18n/format";
import { DatesPassedTag } from "@/features/trips/DatesPassedTag";

/** Status filter: everything, or a single lifecycle state (mirrors the prototype's worklist). */
type StatusFilter = "all" | TripStatus;

const FILTER_OPTIONS: { value: StatusFilter; labelKey: string }[] = [
  { value: "all", labelKey: "trips.filterAll" },
  { value: "Enquiry", labelKey: STATUS_KEY.Enquiry },
  { value: "ProposalSent", labelKey: STATUS_KEY.ProposalSent },
  { value: "Accepted", labelKey: STATUS_KEY.Accepted },
  { value: "Expired", labelKey: STATUS_KEY.Expired },
  { value: "Completed", labelKey: STATUS_KEY.Completed },
  { value: "Postponed", labelKey: STATUS_KEY.Postponed },
];

/** Sort column from the URL; absent/invalid falls back to travel dates (TRAV-71: default = date ascending). */
function readSort(value: string | null): TripSortField {
  return value && (TRIP_SORT_FIELDS as readonly string[]).includes(value)
    ? (value as TripSortField)
    : "dates";
}

/** Margin %: green when healthy (≥15%), amber when thin (≥10%), red below — mirrors the prototype. */
function marginTone(pct: number): string {
  return pct >= 15 ? "var(--success)" : pct >= 10 ? "oklch(0.5 0.12 60)" : "var(--danger)";
}

/**
 * Cross-club all-trips table for back office (TW-3440; finance/export/date-filters added later):
 * every trip across every club, scoped by the topbar club switcher, filterable by status and by a
 * travel-start-date range, with page/sort/dir/status/dates held in the URL. Each row shows the trip's
 * proposal **margin** (TC-internal — folded in server-side for back office only), and Export
 * downloads every trip matching the current filters as a CSV. Rows open the trip detail.
 */
export function OpsTripsPage() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const clubs = useClubLookup();
  const { scopeId } = useClubFilter();
  const [params, setParams] = useSearchParams();

  const page = Math.max(1, Number(params.get("page")) || 1);
  const sort = readSort(params.get("sort"));
  const dir: SortDirection = params.get("dir") === "desc" ? "desc" : "asc";
  const filter: StatusFilter = (params.get("status") as StatusFilter) || "all";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";

  const [data, setData] = useState<TripListPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(
        await tripsApi.listPage({
          clubId: scopeId,
          page,
          pageSize: DEFAULT_TRIP_PAGE_SIZE,
          sort,
          dir,
          status: filter === "all" ? undefined : filter,
          startFrom: from || undefined,
          startTo: to || undefined,
        }),
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [scopeId, page, sort, dir, filter, from, to]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function patch(mutate: (next: URLSearchParams) => void, resetPage = true) {
    const next = new URLSearchParams(params);
    mutate(next);
    if (resetPage) next.delete("page");
    setParams(next);
  }

  // Switching the club scope can leave the current page out of range, so reset to page 1 (but not
  // on first mount, so a shared `?page=` URL still lands where intended).
  const firstScope = useRef(true);
  useEffect(() => {
    if (firstScope.current) {
      firstScope.current = false;
      return;
    }
    if (params.get("page")) patch(() => {});
  }, [scopeId]);

  function onSort(field: string) {
    patch((next) => {
      if (sort === field) {
        next.set("sort", field);
        next.set("dir", dir === "asc" ? "desc" : "asc");
      } else {
        next.set("sort", field);
        next.set("dir", "asc");
      }
    });
  }

  function onFilter(value: StatusFilter) {
    patch((next) => (value === "all" ? next.delete("status") : next.set("status", value)));
  }

  function onDate(which: "from" | "to", value: string) {
    patch((next) => (value ? next.set(which, value) : next.delete(which)));
  }

  function clearDates() {
    patch((next) => {
      next.delete("from");
      next.delete("to");
    });
  }

  function onPage(p: number) {
    patch((next) => (p <= 1 ? next.delete("page") : next.set("page", String(p))), false);
  }

  /**
   * Export every trip matching the current filters (not just the visible page) to a CSV. The list is
   * server-paginated, so we page through the full filtered set (the server caps a page at
   * MAX_TRIP_PAGE_SIZE), localize each row, and let the pure shared builder assemble the bytes.
   */
  async function onExport() {
    setExporting(true);
    try {
      const all: TripDto[] = [];
      for (let p = 1; ; p++) {
        const res = await tripsApi.listPage({
          clubId: scopeId,
          page: p,
          pageSize: MAX_TRIP_PAGE_SIZE,
          sort,
          dir,
          status: filter === "all" ? undefined : filter,
          startFrom: from || undefined,
          startTo: to || undefined,
        });
        all.push(...res.items);
        if (res.items.length === 0 || all.length >= res.total) break;
      }

      const rows: TripExportRow[] = all.map((trip) => ({
        reference: trip.reference,
        club: clubs.name(trip.clubId) ?? "",
        trip: trip.matchOpponent ?? trip.sport ?? "",
        competition: trip.competition ?? "",
        destination:
          [trip.destinationCity, trip.destinationCountry ? fmt.country(trip.destinationCountry) : ""]
            .filter(Boolean)
            .join(", "),
        startDate: trip.travelStartDate ?? "",
        endDate: trip.travelEndDate ?? "",
        pax: trip.paxCount != null ? String(trip.paxCount) : "",
        status: t(STATUS_KEY[trip.status]),
        sellTotal: trip.finance ? String(trip.finance.sellTotal) : "",
        netCost: trip.finance ? String(trip.finance.netCost) : "",
        margin: trip.finance ? String(trip.finance.margin) : "",
        marginPct: trip.finance ? trip.finance.marginPct.toFixed(1) : "",
      }));

      const labels: TripExportLabels = {
        columns: {
          reference: t("worklist.exportCols.reference"),
          club: t("worklist.exportCols.club"),
          trip: t("worklist.exportCols.trip"),
          competition: t("worklist.exportCols.competition"),
          destination: t("worklist.exportCols.destination"),
          startDate: t("worklist.exportCols.startDate"),
          endDate: t("worklist.exportCols.endDate"),
          pax: t("worklist.exportCols.pax"),
          status: t("worklist.exportCols.status"),
          sellTotal: t("worklist.exportCols.sellTotal"),
          netCost: t("worklist.exportCols.netCost"),
          margin: t("worklist.exportCols.margin"),
          marginPct: t("worklist.exportCols.marginPct"),
        },
      };

      // Prepend a UTF-8 BOM so Excel reads accented club/destination names correctly.
      const blob = new Blob(["﻿", buildTripsCsv(rows, labels)], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${t("worklist.exportFilename")}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError(true);
    } finally {
      setExporting(false);
    }
  }

  const filterOptions: SegmentedOption<StatusFilter>[] = FILTER_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }));

  function dates(trip: TripDto): string {
    if (!trip.travelStartDate && !trip.travelEndDate) return "—";
    const start = trip.travelStartDate ? fmt.date(trip.travelStartDate) : "—";
    const end = trip.travelEndDate ? fmt.date(trip.travelEndDate) : "—";
    return `${start} → ${end}`;
  }

  const trips = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? DEFAULT_TRIP_PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const fromRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const toRow = Math.min(page * pageSize, total);

  return (
    <div className="page">
      <div className="page-head row-between">
        <div>
          <h1>{t("worklist.tripsAllTitle")}</h1>
          <div className="sub">{t("worklist.tripsAllSub")}</div>
        </div>
        <Button variant="secondary" icon="download" onClick={onExport} disabled={exporting || total === 0}>
          {t("worklist.export")}
        </Button>
      </div>

      <div className="trips-filter-bar">
        <Segmented options={filterOptions} value={filter} onChange={onFilter} size="sm" />
        <div className="trips-date-filter">
          <label className="tdf-field">
            <span>{t("worklist.dateFrom")}</span>
            <input
              type="date"
              className="input"
              value={from}
              max={to || undefined}
              onChange={(e) => onDate("from", e.target.value)}
            />
          </label>
          <label className="tdf-field">
            <span>{t("worklist.dateTo")}</span>
            <input
              type="date"
              className="input"
              value={to}
              min={from || undefined}
              onChange={(e) => onDate("to", e.target.value)}
            />
          </label>
          {(from || to) && (
            <Button variant="ghost" size="sm" icon="x" onClick={clearDates}>
              {t("worklist.dateClear")}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="empty">{t("common.loading")}</div>
      ) : error ? (
        <div className="empty">{t("trips.loadError")}</div>
      ) : total === 0 ? (
        <div className="empty">
          <div className="empty-ic">
            <Icon name="trips" size={24} />
          </div>
          {t("trips.empty")}
        </div>
      ) : (
        <div className="card card-pad">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortHeader label={t("worklist.colRequest")} field="trip" active={sort === "trip"} dir={dir} onSort={onSort} />
                  <th>{t("worklist.club")}</th>
                  <SortHeader label={t("trips.colDest")} field="destination" active={sort === "destination"} dir={dir} onSort={onSort} />
                  <SortHeader label={t("trips.colDates")} field="dates" active={sort === "dates"} dir={dir} onSort={onSort} />
                  <th>{t("trips.colPax")}</th>
                  <th>{t("trips.colStatus")}</th>
                  <th style={{ textAlign: "right" }}>{t("worklist.colMargin")}</th>
                  <th style={{ textAlign: "right" }}>{t("worklist.colMarginPct")}</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((trip) => (
                  <tr key={trip.id} onClick={() => navigate(`/trips/${trip.id}`)} style={{ cursor: "pointer" }}>
                    <td className="t-trip">
                      <b>{trip.matchOpponent ?? trip.sport ?? trip.reference}</b>
                      <small>{trip.reference}</small>
                    </td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <span className="club-dot" style={{ background: clubs.color(trip.clubId) }} />
                        {clubs.name(trip.clubId) ?? "—"}
                      </span>
                    </td>
                    <td>{[trip.destinationCity, trip.destinationCountry ? fmt.country(trip.destinationCountry) : ""].filter(Boolean).join(", ") || "—"}</td>
                    <td>
                      {dates(trip)}
                      <DatesPassedTag trip={trip} />
                    </td>
                    <td>{trip.paxCount ?? "—"}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <StatusBadge status={TRIP_STATUS_BADGE[trip.status]} label={t(STATUS_KEY[trip.status])} />
                        {isAmendedTrip(trip) && (
                          // A pending club amendment TC hasn't answered — clears on the next publish.
                          <StatusBadge status={TRIP_STATUS_BADGE.Enquiry} label={t("trips.amendedBadge")} />
                        )}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {trip.finance ? (
                        <span className="mono" style={{ fontWeight: 600 }}>
                          {fmt.currency(trip.finance.margin, "EUR", { maximumFractionDigits: 0 })}
                        </span>
                      ) : (
                        <span className="faint mono">—</span>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {trip.finance ? (
                        <span className="mono" style={{ fontWeight: 650, color: marginTone(trip.finance.marginPct) }}>
                          {trip.finance.marginPct.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="faint mono">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            pageCount={pageCount}
            onPage={onPage}
            prevLabel={t("trips.pager.prev")}
            nextLabel={t("trips.pager.next")}
            summary={t("trips.pager.summary", { from: fromRow, to: toRow, total })}
          />
        </div>
      )}
    </div>
  );
}
